import { getGameSession } from "@/api/session";
import { avatarColors } from "@/constants";
import {
  IGameSession,
  IPlayer,
  ICursor,
  IGame,
  IRoomObject,
  IFeedItem,
  IChatItem,
  IActiveQuestionnaire,
  IRoom,
} from "escape-rooms-types/types/game";
import Player from "./Player";
import { createRandomString } from "@/utils/phaser-helpers";
import GameScene from "@/phaser/scenes/GameScene";
import UIScene from "@/phaser/scenes/UIScene";
import EventBridge from "@/utils/EventBridge";

interface Props extends IGameSession {
  gameScene: GameScene;
}

export default class Session {
  gameScene: GameScene;
  uiScene?: UIScene;
  id: string;
  name?: string;
  rawPlayerObjects?: any[];
  players: Player[];
  pickedUpItems?: string[];
  teamCursors?: ICursor[];
  teamScore?: number;
  activeRoomId?: string;
  gameId?: string;
  game?: IGame;
  inventory?: IRoomObject[];
  lockedObjects?: { [key: string]: { playerId: string; roomObjectId: string } };
  timer?: number;
  activityFeed?: IFeedItem[];
  chatFeed?: IChatItem[];
  activeQuestionnaire?: IActiveQuestionnaire;
  status?: string;
  gameStarted?: Date;
  gameCompleted?: Date;
  description?: string;
  callLink?: string;
  myPlayer: Player;

  constructor(gameScene: GameScene, gameSessionData: any) {
    this.id = gameSessionData._id;
    this.gameScene = gameScene;
    this.players = [];
    this.init(gameSessionData);
  }

  init({
    name,
    players,
    pickedUpItems,
    teamCursors,
    teamScore,
    activeRoomId,
    gameId,
    game,
    inventory,
    lockedObjects,
    timer,
    activityFeed,
    chatFeed,
    gameStarted,
    gameCompleted,
    activeQuestionnaire,
    status,
    description,
    callLink,
  }: IGameSession) {
    const myPlayerIndex = players.findIndex(
      (player) => player.playerId == this.gameScene.user.id
    );
    const myPlayer = players[myPlayerIndex];
    if (myPlayer) {
      this.myPlayer = new Player({
        id: myPlayer.playerId,
        score: myPlayer.score,
        clientId: myPlayer.clientId,
        firstName: myPlayer.firstName,
        lastName: myPlayer.lastName,
        color: avatarColors[myPlayerIndex],
        inactive: false,
      });
    }

    this.name = name;
    this.rawPlayerObjects = players;
    this.pickedUpItems = pickedUpItems;
    this.teamCursors = teamCursors;
    this.teamScore = teamScore;
    this.activeRoomId = activeRoomId;
    this.gameId = gameId;
    this.game = game;
    this.inventory = inventory;
    this.lockedObjects = lockedObjects;
    this.timer = timer;
    this.activityFeed = activityFeed;
    this.chatFeed = chatFeed;
    this.activeQuestionnaire = activeQuestionnaire;
    this.status = status;
    this.gameStarted = gameStarted;
    this.gameCompleted = gameCompleted;
    this.description = description;
    this.callLink = callLink;
    this.players = [];
    this.loadPlayers(players);
  }

  loadPlayers(playerJSONArray: any) {
    playerJSONArray.forEach((player: any, index: number) => {
      if (player.playerId === this.gameScene.user.id) {
        this.gameScene.clientId = player.clientId;
        this.myPlayer.clientId = player.clientId;
        this.myPlayer.score = player.playerScore;
      }
      this.addNewPlayer(
        player.playerId,
        player.clientId,
        player.playerScore,
        player.firstName,
        player.lastName,
        avatarColors[index],
        player.inactive,
      );
    });
  }

  // TODO: make this work
  refreshPlayers(playerJSONArray: any) {
    this.players = [];
    this.loadPlayers(playerJSONArray);
  }

  addUiScene(uiScene: UIScene) {
    this.uiScene = uiScene;
    this.refreshProgress();
    this.refreshScore();
    this.populateLiveFeed();
  }

  populateLiveFeed() {
    let messages = this.activityFeed.map((feedItem) => {
      return this.formatFeedItem(feedItem);
    });
    EventBridge.emit("ui.populateLiveFeed", messages);
  }

  formatFeedItem(feedItem: IFeedItem) {
    let subjectName =
      feedItem.subject?.playerId === this.myPlayer.id
        ? "You"
        : `${feedItem.subject?.firstName} ${feedItem.subject?.lastName}`;
    let type = feedItem.feedItemType.templateString;
    let date =
      feedItem.createdAt !== undefined
        ? new Date(Date.parse(feedItem.createdAt))
        : new Date();
    let time = date.toLocaleTimeString("en-uk", {
      hour: "2-digit",
      minute: "2-digit",
    });

    let message: string = "";
    switch (type) {
      case "chat":
        message = `${subjectName}: ${feedItem.object}`;
        break;
      case "useItem":
        message = `${subjectName} used ${feedItem.object.name}`;
        break;
      case "changed":
        message = `${subjectName} changed ${feedItem.object.asset.name} to ${feedItem.object.currentState}`;
        break;
      case "hintUsed":
        message = `${subjectName} used a hint`;
        break;
      case "pickupItem":
        message = `${subjectName} picked up ${feedItem.object.name}`;
        break;
      case "join":
        message = `${subjectName} joined the game`;
        break;
      case "leave":
        message = `${subjectName} left the game`;
        break;
      default:
        console.error("unknown feeditem type");
    }
    message = `${time}: ${message}`;
    return message;
  }

  refreshMyPlayer(player: IPlayer) {
    this.myPlayer.clientId = player.clientId;
    this.myPlayer.score = player.playerScore;
  }

  refreshScore() {
    const playerScore = this.myPlayer.score || 0;
    const teamScore = this.teamScore;

    EventBridge.emit("ui.setScore", {
      playerScore,
      teamScore,
    });
  }

  refreshProgress(objects?: Array<IRoomObject>) {
    let actionsDone: number = 0;
    if (!objects)
      objects = this.game.rooms.find(
        (room) => room._id === this.activeRoomId
      )?.objects;
    objects?.forEach((object) => {
      actionsDone += object.currentAction;
    });
    EventBridge.emit("game.setActionsDone", actionsDone);
  }

  refetchSession = async () => {
    getGameSession(this.id).then((res) => {
      const { players, teamScore, activeQuestionnaire, activeRoomId } =
        res.data.data;
      this.activeRoomId = activeRoomId;
      this.refreshPlayers(players);
      this.refreshProgress(
        res.data.data.game.rooms.find(
          (room: IRoom) => room._id === this.activeRoomId
        )?.objects
      );
      this.refreshScore();
      this.teamScore = teamScore;
      this.activeQuestionnaire = activeQuestionnaire;
      EventBridge.emit("session.refetched");
    });
  };

  // refreshPlayers(players: IPlayer[]) {
  //   this.players.forEach((player) => {
  //     let newPlayerObject = players.find(
  //       (newPlayer) => newPlayer.playerId === player.id
  //     );
  //     if (newPlayerObject) {
  //       player.score = newPlayerObject.playerScore;
  //     }
  //   });
  //   const oldPlayerIds = this.players.map((player) => player.id);
  //   const newPlayerIds = players.map((player) => {
  //     if (player.playerId === this.myPlayer.id) {
  //       this.refreshMyPlayer(player);
  //     }
  //     return player.playerId;
  //   });
  //   const playersToRemove = oldPlayerIds.filter(
  //     (player) => !newPlayerIds.includes(player)
  //   );
  //   const playersToAdd = newPlayerIds.filter(
  //     (player) => !oldPlayerIds.includes(player)
  //   );

  //   if (playersToRemove.length > 0) {
  //     playersToRemove.forEach((playerId) => this.removePlayer(playerId));
  //   }

  //   if (playersToAdd.length > 0) {
  //     playersToAdd.forEach((playerId) => {
  //       const player = players.find((player) => player.playerId === playerId);
  //       if (player == null) {
  //         return console.error(`Player with id ${playerId} not found`);
  //       }
  //       this.addNewPlayer(
  //         player.playerId,
  //         player.clientId,
  //         player.playerScore,
  //         player.firstName,
  //         player.lastName
  //       );
  //     });
  //   }

  //   this.uiScene?.hud?.refreshPlayerAvatars(this.uiScene.hud);
  // }

  getPlayerFromId(playerId: string): Player | undefined {
    const player = this.players.find((player) => player.id === playerId);
    if (player == null) {
      console.error(`Player with id ${playerId} not found`);
      return undefined;
    }
    return player;
  }

  getPlayerFromClientId(clientId: string): Player | undefined {
    const player = this.players.find((player) => player.clientId === clientId);
    if (player == null) {
      return undefined;
    }
    return player;
  }

  addNewPlayer(
    id: string,
    clientId: string,
    score: number,
    firstName: string,
    lastName: string,
    color: number,
    inactive: boolean,
  ) {
    if (this.players.map((player) => player.id).includes(id)) {
      return;
    }
    const newPlayer = new Player({
      id,
      score,
      clientId,
      firstName,
      lastName,
      color,
      inactive,
    });
    this.players.push(newPlayer);
  }

  removePlayer(playerId: string) {
    this.players = this.players.filter((player) => player.id !== playerId);
  }

  markPlayerInactive(playerId: string) {
    const targetPlayer = this.players.find((player) => player.id == playerId);
    targetPlayer?.setInactive(true)
    this.uiScene?.hud?.refreshPlayerAvatars(this.uiScene.hud);
  }

  markPlayerActive(playerId: string) {
    const targetPlayer = this.players.find((player) => player.id == playerId);
    targetPlayer?.setInactive(false)
    this.uiScene?.hud?.refreshPlayerAvatars(this.uiScene.hud);
  }
}
